/* P97 — gift progress strip inside the slide-out cart.

   WHAT IT IS
   A full-width bar directly under the drawer's "Your Cart" heading: progress
   toward the next gift tier, the amount still to spend, and a thumbnail of the
   gift itself. Same ladder as the floating widget, shown at the moment it
   actually changes behaviour — when someone is looking at their cart.

   WHERE THE TIERS COME FROM
   The p97-gift-rewards section already renders every tier: row amount, gift
   name and photo. We read that DOM instead of re-declaring thresholds here, so
   Customize > Gift Rewards stays the single place to edit them and the two can
   never drift apart.

   Re-read on every render (Nov 2 2026): reading once and caching could catch the
   widget before it had written its labels, so a bundle tier (e.g. $225 = 3 gifts)
   came through with no name and showed "unlock your gift". Bundle tiers now read
   "unlock 3 more gifts" (Marcos). The message stays on one line (type shrinks a
   little on narrow phones instead of wrapping).

   WHAT IT DOES NOT DO
   Never writes to the cart, never opens or closes the drawer, never touches
   Add To Cart. The drawer belongs to udesly — this appends one element to a
   static header and keeps its text current.
*/
(function () {
  'use strict';

  var HEADER = '.w-commerce-commercecartheader';
  var CONTAINER = '[data-node-type="commerce-cart-container"]';

  var el = null;      // our strip
  var tiers = null;   // [{threshold, name, img, count}]

  function money(n) { return '$' + Math.ceil(n); }

  /* Read the ladder the gift widget already rendered. */
  function readTiers() {
    var rows = document.querySelectorAll('#p97grList .p97gr-row');
    if (!rows.length) return null;

    var out = [];
    Array.prototype.forEach.call(rows, function (row) {
      var amtEl = row.querySelector('.p97gr-amt');
      if (!amtEl) return;
      var amt = parseFloat((amtEl.textContent || '').replace(/[^0-9.]/g, ''));
      if (isNaN(amt)) return;

      var giftEl = row.querySelector('.p97gr-gift');
      var name = '';
      if (giftEl) {
        var lbl = giftEl.querySelector('.p97gr-giftlbl');
        name = (lbl ? lbl.textContent : giftEl.textContent) || '';
        name = name.replace(/[▾▼]/g, '').replace(/\s+/g, ' ').trim();
      }

      /* bundle tiers: "3 free gifts" -> "3 more gifts" */
      var count = 0;
      var m = name.match(/^(\d+)\s+free gifts?$/i);
      if (m) count = +m[1];
      var thumbs = row.querySelector('.p97gr-thumbs');
      if (!name && thumbs && thumbs.classList.contains('multi')) {
        count = thumbs.querySelectorAll('img').length;
      }
      if (count > 1) name = count + ' more gifts';

      var img = row.querySelector('.p97gr-thumbs img');
      out.push({ threshold: amt, name: name, img: img ? img.getAttribute('src') : null });
    });

    if (!out.length) return null;
    out.sort(function (a, b) { return a.threshold - b.threshold; });
    return out;
  }

  function build() {
    if (el && el.isConnected) return el;

    var container = document.querySelector(CONTAINER);
    if (!container) return null;
    var header = container.querySelector(HEADER);
    if (!header) return null;

    el = container.querySelector('[data-p97cp]');
    if (el) return el;

    el = document.createElement('div');
    el.setAttribute('data-p97cp', '');
    el.className = 'p97cp';
    el.hidden = true;
    el.innerHTML =
      '<div class="p97cp__row">' +
        '<div class="p97cp__thumb" data-p97cp-thumb></div>' +
        '<div class="p97cp__copy">' +
          '<div class="p97cp__msg" data-p97cp-msg></div>' +
          '<div class="p97cp__track"><div class="p97cp__fill" data-p97cp-fill></div></div>' +
        '</div>' +
      '</div>';

    /* directly under the heading, above the first item */
    header.insertAdjacentElement('afterend', el);
    return el;
  }

  function render(total) {
    tiers = readTiers() || tiers;
    if (!tiers) return;

    var strip = build();
    if (!strip) return;

    if (!total || total <= 0) { strip.hidden = true; return; }

    var next = null;
    for (var i = 0; i < tiers.length; i++) {
      if (total < tiers[i].threshold) { next = tiers[i]; break; }
    }

    var msg = strip.querySelector('[data-p97cp-msg]');
    var fill = strip.querySelector('[data-p97cp-fill]');
    var thumb = strip.querySelector('[data-p97cp-thumb]');

    if (next) {
      var remaining = next.threshold - total;
      msg.innerHTML = 'Spend <b>' + money(remaining) + '</b> more to unlock ' +
                      '<span class="p97cp__gift">' + (next.name || 'your gift') + '</span>';
      thumb.innerHTML = next.img ? '<img alt="" src="' + next.img + '">' : '';
      thumb.hidden = !next.img;

      /* fill the span between the tier just passed and the next one, so the bar
         reads as progress toward THIS gift rather than the whole ladder */
      var prev = 0;
      for (var j = 0; j < tiers.length; j++) {
        if (tiers[j].threshold < next.threshold) prev = tiers[j].threshold;
      }
      var span = next.threshold - prev;
      var pct = span > 0 ? ((total - prev) / span) * 100 : 0;
      fill.style.width = Math.max(2, Math.min(100, pct)) + '%';
      strip.classList.remove('is-complete');
    } else {
      msg.innerHTML = '<b>All gifts unlocked</b> — they are in your bag';
      thumb.innerHTML = '';
      thumb.hidden = true;
      fill.style.width = '100%';
      strip.classList.add('is-complete');
    }

    strip.hidden = false;
    fitOneLine(msg);
  }

  /* keep the message on ONE line (Marcos, Nov 2 2026): shrink the type a little on narrow
     phones instead of wrapping to a second line */
  function fitOneLine(msg) {
    if (!msg) return;
    msg.style.whiteSpace = 'nowrap';
    msg.style.overflow = 'hidden';
    msg.style.textOverflow = 'ellipsis';
    msg.style.fontSize = '';
    msg.style.letterSpacing = '';
    if (!msg.clientWidth) return;
    var size = parseFloat(getComputedStyle(msg).fontSize) || 11;
    var tries = 0;
    while (msg.scrollWidth > msg.clientWidth + 1 && size > 8 && tries < 12) {
      size -= 0.5; tries++;
      msg.style.fontSize = size + 'px';
      if (size <= 9.5) msg.style.letterSpacing = '.06em';
    }
  }

  var fetching = false;
  function refresh() {
    if (fetching) return;
    fetching = true;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { fetching = false; render(c.total_price / 100); })
      .catch(function () { fetching = false; /* leave the strip as it is */ });
  }

  var pending = null;
  function schedule() {
    clearTimeout(pending);
    pending = setTimeout(refresh, 120);
  }

  function boot() {
    /* drawer and gift widget both render after us; keep trying briefly */
    var tries = 0;
    var timer = setInterval(function () {
      tries++;
      if (build() || tries > 40) clearInterval(timer);
    }, 300);

    refresh();
    setInterval(refresh, 4000);

    ['cart:refresh', 'cart:updated', 'cart:change'].forEach(function (n) {
      document.addEventListener(n, schedule);
    });

    var open = document.querySelector('[data-node-type="commerce-cart-open-link"]');
    if (open) open.addEventListener('click', schedule);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
